import { useMemo } from "react";
import { useEntryFiltersCache, useMattersCache } from "@/shared/cache";
import { useApplicationsCache } from "@/shared/cache/slices/applications";
import type { Entry } from "../normalize";
import { formatTimekeeperFullName, type Timekeeper } from "../timekeepers";
import type { FieldOptions } from "./FilterMenu";
import { FIELD_DEFS, type OptionItem } from "./fields";
import type { EnumFilterField } from "./types";

const HIDDEN_APPS = new Set(["", "Phone", "Hourglass"]);
const BOTTOM_APPS = ["User Input", "Duplicate", "Merge", "Collapse", "Group"];
const BOTTOM_APP_ORDER = new Map(BOTTOM_APPS.map((name, i) => [name, i]));

// Shared collator. Inline `localeCompare(undefined, { sensitivity: "base" })`
// builds a fresh collator per call; on a firm-sized matter list (5k+) that
// adds ~150ms per sort. Reusing one instance drops it to a few ms.
const COLLATOR = new Intl.Collator(undefined, { sensitivity: "base" });

function sortAppNames(names: Iterable<string>): string[] {
	return Array.from(names).sort((a, b) => {
		const ai = BOTTOM_APP_ORDER.get(a);
		const bi = BOTTOM_APP_ORDER.get(b);
		if (ai !== undefined && bi !== undefined) return ai - bi;
		if (ai !== undefined) return 1;
		if (bi !== undefined) return -1;
		return COLLATOR.compare(a, b);
	});
}

function distinct<T>(
	items: ReadonlyArray<T>,
	select: (item: T) => string | null | undefined,
): string[] {
	const values = new Set<string>();
	for (const item of items) {
		const value = select(item);
		if (value) values.add(value);
	}
	return Array.from(values).sort(COLLATOR.compare);
}

/**
 * Build the value option list per enum filter field. Static fields use the
 * registry (FIELD_DEFS); data-driven fields are deduped + sorted from loaded
 * entries. Applications come from the canonical /applications cache. Matters
 * and clients come from the firm matters cache and are unioned with
 * entry-derived values so that orphan codes (matters referenced by an entry
 * but no longer in the cache) still appear.
 */
export function useFilterOptions(
	entries: ReadonlyArray<Entry>,
	timekeepers?: readonly Timekeeper[] | null,
): FieldOptions {
	const { applications: knownApplications } = useApplicationsCache();
	const { matters: cachedMatters } = useMattersCache();
	const { entryFilters } = useEntryFiltersCache();

	return useMemo(() => {
		const staticOptions: Partial<
			Record<EnumFilterField, ReadonlyArray<string>>
		> = {};
		for (const def of FIELD_DEFS) {
			if (def.kind !== "enum") continue;
			if (def.source === "static" && def.staticValues) {
				staticOptions[def.key] = def.staticValues;
			}
		}

		const appNames = new Set<string>();
		for (const a of knownApplications) {
			if (a.application && !HIDDEN_APPS.has(a.application)) {
				appNames.add(a.application);
			}
		}
		for (const e of entries) {
			if (e.application && !HIDDEN_APPS.has(e.application)) {
				appNames.add(e.application);
			}
		}

		// Filter value is the stable backend matter id. Labels remain the user-
		// visible matter code/description.
		const matterById = new Map<string, OptionItem>();
		for (const m of cachedMatters) {
			const id = (m.matter_id ?? "").trim();
			if (!id) continue;
			if (!matterById.has(id)) {
				const sub = [m.description?.trim(), m.client?.trim()]
					.filter(Boolean)
					.join(" · ");
				matterById.set(id, {
					value: id,
					label: m.matter || id,
					sublabel: sub || undefined,
					description: m.description,
					client: m.client,
				});
			}
		}
		for (const e of entries) {
			const id = e.matter?.id ?? null;
			if (!id) continue;
			if (matterById.has(id)) continue;
			matterById.set(id, {
				value: id,
				label: e.matter?.code || id,
				sublabel: e.matter?.client ?? undefined,
				client: e.matter?.client ?? undefined,
			});
		}
		const matterOptions = Array.from(matterById.values()).sort((a, b) =>
			COLLATOR.compare(a.label, b.label),
		);

		const clientSet = new Set<string>();
		for (const m of cachedMatters) if (m.client) clientSet.add(m.client);
		for (const e of entries)
			if (e.matter?.client) clientSet.add(e.matter.client);
		const clientOptions = Array.from(clientSet).sort(COLLATOR.compare);

		const timekeeperOptions = timekeepers?.map((timekeeper) => ({
			value: timekeeper.id,
			label: formatTimekeeperFullName(timekeeper),
			sublabel: timekeeper.email,
		}));

		const applicationOptions =
			entryFilters.application.length > 0
				? sortAppNames(
						entryFilters.application.filter((name) => !HIDDEN_APPS.has(name)),
					)
				: sortAppNames(appNames);
		// The server owns the available values, but its facet rows may only
		// contain codes. Hydrate search/display metadata from the matter catalog.
		const serverMatterOptions = entryFilters.matter.map((option) => {
			const known = matterById.get(option.value);
			const description =
				option.description || known?.description || option.sublabel;
			const client = option.client || known?.client;
			const sublabel = [description, client].filter(Boolean).join(" · ");
			return {
				...option,
				description,
				client,
				sublabel: sublabel || option.sublabel || known?.sublabel,
			};
		});
		const clientKeywords = new Map<string, Set<string>>();
		for (const option of [...matterOptions, ...serverMatterOptions]) {
			if (!option.client) continue;
			const keywords = clientKeywords.get(option.client) ?? new Set<string>();
			keywords.add(option.label);
			if (option.description) keywords.add(option.description);
			clientKeywords.set(option.client, keywords);
		}
		const searchableClients = (
			entryFilters.client.length > 0 ? entryFilters.client : clientOptions
		).map((client) => ({
			value: client,
			label: client,
			keywords: [...(clientKeywords.get(client) ?? [])],
		}));
		const serverTimekeeperOptions =
			entryFilters.timekeeper.length > 1
				? (entryFilters.timekeeper as ReadonlyArray<OptionItem>)
				: undefined;

		return {
			...staticOptions,
			...(entryFilters.status.length > 0
				? { status: entryFilters.status }
				: {}),
			...(entryFilters.billableCategory.length > 0
				? { billableCategory: entryFilters.billableCategory }
				: {}),
			...(timekeeperOptions && timekeeperOptions.length > 0
				? { timekeeper: timekeeperOptions }
				: serverTimekeeperOptions
					? { timekeeper: serverTimekeeperOptions }
					: {}),
			application: applicationOptions,
			matter:
				serverMatterOptions.length > 0 ? serverMatterOptions : matterOptions,
			client: searchableClients,
			task:
				entryFilters.task.length > 0
					? entryFilters.task
					: distinct(entries, (e) => e.codes?.task),
			activity:
				entryFilters.activity.length > 0
					? entryFilters.activity
					: distinct(entries, (e) => e.codes?.activity),
		};
	}, [entries, knownApplications, cachedMatters, timekeepers, entryFilters]);
}
